import type { ParsedToken } from '../../../types/QueryBuilder';
import { parseQuery } from '../parseQuery';

export type GroupEdit = {
  query: string;
  parenOffset: number;
};

type GroupEditOptions = {
  allowThenOperator?: boolean;
};

function normalize(query: string): string {
  let out = '';
  let inQuote = false;
  let pendingSpace = false;
  for (const character of query) {
    if (character === '"') {
      if (pendingSpace && out && !inQuote) {
        out += ' ';
      }
      pendingSpace = false;
      inQuote = !inQuote;
      out += character;
      continue;
    }
    if (!inQuote && /\s/.test(character)) {
      pendingSpace = out.length > 0;
      continue;
    }
    if (pendingSpace) {
      out += ' ';
      pendingSpace = false;
    }
    out += character;
  }
  return out.trim();
}

function append(body: string, next: string): string {
  if (!next) {
    return body;
  }
  if (!body) {
    return next;
  }
  return next.startsWith(')') ? `${body}${next}` : `${body} ${next}`;
}

function containsOffset(token: ParsedToken, offset: number): boolean {
  return offset > token.location.start.offset && offset < token.location.end.offset;
}

function followingContent(tokens: ParsedToken[], offset: number): ParsedToken[] | null {
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === 'EXPRESSION' && containsOffset(token, offset)) {
      return followingContent(token.inner, offset);
    }
    if (token.location.start.offset >= offset) {
      const tail = tokens.slice(i);
      const content = tail[0]?.type === 'LOGIC_BOOL' ? tail.slice(1) : tail;
      return content.length > 0 ? content : null;
    }
  }
  return null;
}

function groupOffset(next: string, head: string, group: string, edge: 'open' | 'close'): number {
  const normalizedHead = normalize(head);
  const normalizedGroup = normalize(group);
  const marker = normalizedHead ? `${normalizedHead} ${normalizedGroup}` : normalizedGroup;
  const at = next.indexOf(marker);
  if (at < 0) {
    return -1;
  }
  return edge === 'close' ? at + marker.length - 1 : at + marker.length - normalizedGroup.length;
}

export function wrapFollowingAt(
  query: string,
  offset: number,
  { allowThenOperator = false }: GroupEditOptions = {},
): GroupEdit | null {
  const content = followingContent(parseQuery(query, { allowThenOperator }), offset);
  if (!content) {
    return null;
  }
  const rangeStart = content[0].location.start.offset;
  const rangeEnd = content[content.length - 1].location.end.offset;
  const inner = query.slice(rangeStart, rangeEnd).trim();
  if (!inner) {
    return null;
  }
  const head = query.slice(0, rangeStart).trimEnd();
  const tail = query.slice(rangeEnd).trim();
  const group = `(${inner})`;
  const next = normalize(append(append(head, group), tail));
  const parenOffset = groupOffset(next, head, group, 'open');
  if (parenOffset < 0) {
    return null;
  }
  return { query: next, parenOffset };
}

type ClosePoint = {
  expression: Extract<ParsedToken, { type: 'EXPRESSION' }>;
  keepEnd: number;
  restStart: number;
};

function closePoint(tokens: ParsedToken[], offset: number): ClosePoint | null {
  for (const token of tokens) {
    if (token.type !== 'EXPRESSION' || !containsOffset(token, offset)) {
      continue;
    }
    const nested = token.inner.some((inner) => inner.type === 'EXPRESSION' && containsOffset(inner, offset));
    if (nested) {
      return closePoint(token.inner, offset);
    }
    const keep = token.inner.filter((inner) => inner.location.start.offset < offset);
    const rest = token.inner.filter((inner) => inner.location.start.offset >= offset);
    while (keep.length > 0 && keep[keep.length - 1].type === 'LOGIC_BOOL') {
      const connector = keep.pop();
      if (connector) {
        rest.unshift(connector);
      }
    }
    if (keep.length === 0 || rest.length === 0) {
      return null;
    }
    return {
      expression: token,
      keepEnd: keep[keep.length - 1].location.end.offset,
      restStart: rest[0].location.start.offset,
    };
  }
  return null;
}

export function closeGroupAt(
  query: string,
  offset: number,
  { allowThenOperator = false }: GroupEditOptions = {},
): GroupEdit | null {
  const point = closePoint(parseQuery(query, { allowThenOperator }), offset);
  if (!point) {
    return null;
  }
  const { expression, keepEnd, restStart } = point;
  const prefix = expression.expressionType ?? '';
  const openEnd = expression.location.start.offset + prefix.length + 1;
  const kept = query.slice(openEnd, keepEnd).trim();
  const moved = query.slice(restStart, expression.location.end.offset - 1).trim();
  if (!kept || !moved) {
    return null;
  }
  const head = query.slice(0, expression.location.start.offset).trimEnd();
  const tail = query.slice(expression.location.end.offset).trim();
  const group = `${prefix}(${kept})`;
  const next = normalize(append(append(append(head, group), moved), tail));
  const parenOffset = groupOffset(next, head, group, 'close');
  if (parenOffset < 0) {
    return null;
  }
  return { query: next, parenOffset };
}
